// AI and models (reset PR3: DS artboard 1h / ui_kits/desktop/SettingsScreen.jsx) as three slot
// rows — speech-to-text, speaker labels, language model — each with its own provider. Only
// speech-to-text is wired today (FR-AI7 / FR-BY1 / FR-BY5); speaker labels (diarization, F4)
// and the language model (F1) say "Not available yet" instead of pretending.
// F4: speech-to-text and speaker labels are now slot-aware (personal → org → hosted Orchardrun);
// each slot row resolves from `settings.slots` and has Change / Test / On-Off actions.
// F1: the language model is now connectable (Language model card below; no hosted default).
// The page is split into tabs (Overview, Speech-to-text, Language model, Ask across meetings,
// Access) kept in `?tab=`; the Overview flags anything switched on but not working, with a
// "View details" that opens the tab holding the fix.
// transcription.rs:
// GET/PATCH /orgs/{org_id}/transcription, GET/POST /orgs/{org_id}/transcription/providers,
// PATCH/DELETE /.../providers/{id}, POST .../test, POST .../activate,
// PATCH /orgs/{org_id}/members/{user_id}/transcription.
import { useCallback, useEffect, useState, type ReactNode } from "react";
import { Link, useNavigate, useOutletContext, useSearchParams } from "react-router-dom";
import ConfirmDialog from "../components/ConfirmDialog";
import LlmProviderPicker, { LLM_KIND_LABEL, type LlmConnectBody, type LlmUpdateBody } from "../components/LlmProviderPicker";
import { api, ApiFail } from "../api/client";
import type {
  AskSettingsOut,
  LastNotesErrorOut,
  LlmProvidersList,
  LlmTestOut,
  TranscriptionGrantOut,
  TranscriptionProviderKind,
  TranscriptionProviderOut,
  TranscriptionSettingsOut,
  TranscriptionSlotOut,
  TranscriptionTestOut,
  TranscriptionWhoMayInvite,
  TranscriptionWhoMayUse,
} from "../api/types";
import { Button, Field, Notice } from "../components/ui";
import { getAskSettings, putAskSettings } from "../ask/api";
import { Badge, Callout, Card, Icon, Select, Tabs, ToggleRow } from "@grackle/ui";
import type { AdminOutletContext } from "./AdminLayout";

const KIND_LABEL: Record<TranscriptionProviderKind, string> = {
  openai_whisper_compatible: "OpenAI Whisper-compatible",
  groq_whisper: "Groq Whisper",
  openai_audio: "OpenAI audio",
  orchard: "Orchardrun",
};

const KIND_DEFAULTS: Record<TranscriptionProviderKind, { base: string; model: string }> = {
  openai_whisper_compatible: { base: "https://api.openai.com/v1", model: "whisper-1" },
  groq_whisper: { base: "https://api.groq.com/openai/v1", model: "whisper-large-v3-turbo" },
  openai_audio: { base: "https://api.openai.com/v1", model: "gpt-4o-mini-transcribe" },
  orchard: { base: "https://api.orchardrun.com/v1", model: "orchard" },
};

// F4: STT accepts any of the four kinds; diarization is Orchard-only because it is the only
// adapter that returns speaker turns (server/src/transcription.rs Slot::kinds). The `llm` slot
// has no adapters yet (F1) and is not configurable on this page.
const SLOT_KINDS: Record<"stt" | "diarization", TranscriptionProviderKind[]> = {
  stt: ["openai_whisper_compatible", "groq_whisper", "openai_audio", "orchard"],
  diarization: ["orchard"],
};

const SLOT_LABEL: Record<"stt" | "diarization", string> = {
  stt: "Speech-to-text",
  diarization: "Speaker labels",
};

function kindLabel(kind: string): string {
  return KIND_LABEL[kind as TranscriptionProviderKind] ?? kind;
}

type AiTab = "overview" | "stt" | "llm" | "ask" | "access";

export const AI_TABS: { value: AiTab; label: string }[] = [
  { value: "overview", label: "Overview" },
  { value: "stt", label: "Speech-to-text" },
  { value: "llm", label: "Language model" },
  { value: "ask", label: "Ask across meetings" },
  { value: "access", label: "Access" },
];

function isAiTab(value: string | null): value is AiTab {
  return AI_TABS.some((t) => t.value === value);
}

/** Something on this page is set up but not working; the Overview lists these with a way to the fix. */
export interface AiFlag {
  id: string;
  tone: "danger" | "now";
  message: string;
  tab: AiTab;
  /** Which provider list on the Speech-to-text tab the fix is in. */
  slot?: "stt" | "diarization";
  /** When the fix isn't on a tab here: an in-app path, and the button label for it. */
  to?: string;
  action?: string;
}

/** A notes failure is a warning until this many recordings in a row have failed; then the model
 * is likely broken, not one meeting. A constant, not a setting (SD58). */
export const NOTES_FAILURES_BROKEN = 3;

function sentence(reason: string | null, fallback: string): string {
  const text = (reason ?? "").trim() || fallback;
  return text.charAt(0).toUpperCase() + text.slice(1);
}

/** "Notes for "Weekly sync" failed to generate (date): reason" — shared by both flag surfaces. */
export function notesErrorText(e: LastNotesErrorOut): string {
  const which = e.title ? ` for "${e.title}"` : "";
  const at = e.at ? ` (${new Date(e.at).toLocaleString()})` : "";
  return `Meeting notes${which} failed to generate${at}: ${e.error}`;
}

/** A slot switched off on purpose is not a failure; one that is on and resolves to nothing is. */
export function aiFailureFlags(
  settings: TranscriptionSettingsOut,
  ask: AskSettingsOut | null,
  lastNotesError: LastNotesErrorOut | null,
): AiFlag[] {
  const flags: AiFlag[] = [];
  const slot = (name: string) => settings.slots.find((x) => x.slot === name) ?? null;
  const stt = slot("stt");
  const diar = slot("diarization");
  const llm = slot("llm");
  if (settings.enabled && stt && stt.enabled && stt.status !== "available") {
    flags.push({
      id: "stt",
      tone: "danger",
      message: `Speech-to-text isn't working. ${sentence(stt.reason, "No provider is available")}.`,
      tab: "stt",
      slot: "stt",
    });
  }
  if (settings.enabled && settings.diarization_enabled && diar && diar.enabled && diar.status !== "available") {
    flags.push({
      id: "diarization",
      tone: "danger",
      message: `Speaker labels aren't working. ${sentence(diar.reason, "No provider is available")}.`,
      tab: "stt",
      slot: "diarization",
    });
  }
  if (lastNotesError?.error) {
    // The server only sends a failure that still matters (recent, nothing has succeeded since,
    // not canceled), so this flag clears itself. It points at the recording, where Retry and
    // Cancel are; the model settings are only the fix when failures keep coming.
    const others = Math.max(0, (lastNotesError.recent_failures ?? 1) - 1);
    flags.push({
      id: "notes",
      tone: others + 1 >= NOTES_FAILURES_BROKEN ? "danger" : "now",
      message: `${notesErrorText(lastNotesError)}${
        others ? ` ${others} other recent meeting${others === 1 ? "" : "s"} also failed.` : ""
      }`,
      tab: "llm",
      ...(lastNotesError.grackle_id ? { to: `/g/${lastNotesError.grackle_id}`, action: "Open recording" } : {}),
    });
  }
  if (ask?.ask_across_meetings && llm?.status !== "available") {
    flags.push({
      id: "ask",
      tone: "now",
      message: "Ask across meetings is on, but no language model is connected, so members can't use it yet.",
      tab: "llm",
    });
  }
  return flags;
}

export default function AiSettingsPage() {
  const { org } = useOutletContext<AdminOutletContext>();
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  // The tab is in the URL (like the account page) so the Overview's "View details" and a shared
  // link both land on the right section.
  const rawTab = params.get("tab");
  const tab: AiTab = isAiTab(rawTab) ? rawTab : "overview";
  const focusSlot = params.get("slot") === "diarization" ? "diarization" : "stt";

  const [settings, setSettings] = useState<TranscriptionSettingsOut | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [ask, setAsk] = useState<AskSettingsOut | null>(null);
  const [askLoadError, setAskLoadError] = useState<string | null>(null);
  const [lastNotesError, setLastNotesError] = useState<LastNotesErrorOut | null>(null);

  const load = useCallback(() => {
    api<TranscriptionSettingsOut>("GET", `/orgs/${org.id}/transcription`)
      .then((next) => {
        setSettings(next);
        setLoadError(null);
      })
      .catch((err) => setLoadError(err instanceof ApiFail ? err.message : "Couldn't load AI settings."));
    // Admin-only, optional surface (decision 3) — a non-admin 404 here is expected and quiet.
    api<LastNotesErrorOut>("GET", `/orgs/${org.id}/notes/last-error`)
      .then(setLastNotesError)
      .catch(() => setLastNotesError(null));
  }, [org.id]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    getAskSettings(org.id)
      .then((next) => {
        setAsk(next);
        setAskLoadError(null);
      })
      .catch((err) => setAskLoadError(err instanceof ApiFail ? err.message : "Couldn't load Ask settings."));
  }, [org.id]);

  // "Saved." and save errors belong to the tab they happened on.
  useEffect(() => {
    setSaved(false);
    setSaveError(null);
  }, [tab]);

  function goTo(next: AiTab, slot?: "stt" | "diarization") {
    const nextParams = new URLSearchParams(params);
    nextParams.set("tab", next);
    if (slot === "diarization") nextParams.set("slot", slot);
    else nextParams.delete("slot");
    setParams(nextParams, { replace: true });
  }

  async function patchSettings(body: TranscriptionSettingsPatch) {
    setSaveError(null);
    setSaved(false);
    try {
      const next = await saveTranscriptionSettings(org.id, body);
      setSettings(next);
      setSaved(true);
    } catch (err) {
      setSaveError(err instanceof ApiFail ? err.message : "Couldn't save that.");
    }
  }

  const head = (
    <div className="ai-head">
      <h1 className="gk-h2">AI and models</h1>
      <p className="gk-body ai-head__lead">
        Speech-to-text, speaker labels and the language model each run on their own provider. Recordings,
        sharing and search work without any of them.
      </p>
    </div>
  );

  if (loadError && !settings) {
    return (
      <div className="ai-settings">
        {head}
        <Callout tone="danger" icon="warning-circle">
          {loadError}
        </Callout>
        <div>
          <Button variant="secondary" onClick={load}>
            Retry
          </Button>
        </div>
      </div>
    );
  }

  if (!settings) return <div className="admin-row-sub">Loading…</div>;

  const sttSlot = settings.slots.find((x) => x.slot === "stt") ?? null;
  const diarSlot = settings.slots.find((x) => x.slot === "diarization") ?? null;
  const llmSlot = settings.slots.find((x) => x.slot === "llm") ?? null;
  const flags = aiFailureFlags(settings, ask, lastNotesError);

  const saveFeedback = (
    <>
      {saveError ? (
        <Callout tone="danger" icon="warning-circle">
          {saveError}
        </Callout>
      ) : null}
      {saved ? <div className="gk-caption ai-saved">Saved.</div> : null}
    </>
  );

  return (
    <div className="ai-settings">
      {head}
      <Tabs variant="pill" items={AI_TABS} value={tab} onChange={(v) => goTo(v as AiTab)} className="ai-tabs" />

      <div className="ai-tabpanel" role="tabpanel" aria-label={AI_TABS.find((t) => t.value === tab)?.label}>
        {tab === "overview" ? (
          <>
            {flags.map((f) => (
              <Callout
                key={f.id}
                tone={f.tone}
                icon={f.tone === "danger" ? "warning-circle" : "info"}
                className="ai-flag"
              >
                <span>{f.message}</span>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => (f.to ? navigate(f.to) : goTo(f.tab, f.slot))}
                >
                  {f.action ?? "View details"}
                </Button>
              </Callout>
            ))}

            {/* FR-AI1a: personal, then org, then hosted. FR-AI2a: never a hosted language model. */}
            <Card title="Providers" className="ai-slots">
              <div className="gk-sm" style={{ marginBottom: 8 }}>
                Each slot resolves in order: a personal account (if allowed), then this org's connected account,
                then the Grackle-hosted default for speech-to-text and speaker labels only — never for language
                models.
              </div>
              <div role="list" className="ai-slots__list">
                {sttSlot ? (
                  <ConfigurableSlotRow
                    orgId={org.id}
                    slot="stt"
                    icon="waveform"
                    slotOut={sttSlot}
                    onChange={() => goTo("stt", "stt")}
                    onToggleEnabled={(v) => void patchSettings(slotTogglePatch("stt", v))}
                  />
                ) : null}
                {diarSlot ? (
                  <ConfigurableSlotRow
                    orgId={org.id}
                    slot="diarization"
                    icon="user-sound"
                    slotOut={diarSlot}
                    onChange={() => goTo("stt", "diarization")}
                    onToggleEnabled={(v) => void patchSettings(slotTogglePatch("diarization", v))}
                  />
                ) : null}
                <LanguageModelSlotRow slotOut={llmSlot} onChange={() => goTo("llm")} />
              </div>
              {/* The slot rows' On / Off save here too. */}
              {saveFeedback}
            </Card>

            <Callout tone="info" icon="info">
              Transcription is optional. With it off, or with no provider connected, recordings, sharing, and search
              still work. Nothing is sent to a model until someone clicks Transcribe on a recording.
            </Callout>
          </>
        ) : null}

        {tab === "stt" ? (
          <>
            <Card title="Speech-to-text">
              <ToggleRow
                switchPosition="start"
                label="Enable transcription"
                description="Uses the active provider. Turning this off does not delete connected providers."
                checked={settings.enabled}
                onChange={(v) => void patchSettings({ enabled: v })}
              />
              <ToggleRow
                switchPosition="start"
                label="Enable speaker labels"
                description="Labels speakers in the transcript (“Speaker 1”). Only ever runs behind a transcript, so it has no effect while transcription is off."
                checked={settings.diarization_enabled}
                onChange={(v) => void patchSettings({ diarization_enabled: v })}
              />
              {saveFeedback}
            </Card>

            <ProvidersCard
              orgId={org.id}
              slot="stt"
              providers={settings.providers.filter((p) => p.slot === "stt")}
              focus={focusSlot === "stt"}
              onChanged={load}
            />
            {diarSlot ? (
              <ProvidersCard
                orgId={org.id}
                slot="diarization"
                providers={settings.providers.filter((p) => p.slot === "diarization")}
                focus={focusSlot === "diarization"}
                onChanged={load}
              />
            ) : null}
          </>
        ) : null}

        {tab === "llm" ? (
          <Card title="Language model">
            <LanguageModelSection orgId={org.id} lastNotesError={lastNotesError} onChanged={load} />
          </Card>
        ) : null}

        {tab === "ask" ? (
          <Card title="Ask across meetings">
            <AskSettingsSection orgId={org.id} settings={ask} loadError={askLoadError} onSaved={setAsk} />
          </Card>
        ) : null}

        {tab === "access" ? (
          <>
            {/* The policy dropdowns lead the card; its heading sits above what they decide (the
                grants table or the one-line summary), drawn the way Card draws a title. */}
            <Card>
              <div className="admin-stack">
                <div className="ai-fields">
                  <div className="gk-field">
                    <Select
                      id="who-may-use"
                      label="Who may transcribe"
                      value={settings.who_may_use}
                      onChange={(v) => void patchSettings({ who_may_use: v as TranscriptionWhoMayUse })}
                      options={[
                        { value: "admins", label: "Owners and admins only" },
                        { value: "members", label: "Every member (not guests)" },
                        { value: "allowlist", label: "Specific people" },
                      ]}
                    />
                    <div className="gk-field__help">Guests never use org tokens. Owners and admins always can.</div>
                  </div>
                  <div className="gk-field">
                    <Select
                      id="who-may-invite"
                      label="Who may invite others onto these tokens"
                      value={settings.who_may_invite}
                      onChange={(v) => void patchSettings({ who_may_invite: v as TranscriptionWhoMayInvite })}
                      options={[
                        { value: "admins", label: "Owners and admins only" },
                        { value: "allowlist", label: "Specific people" },
                      ]}
                    />
                    <div className="gk-field__help">Granting the right to invite stays admin-only even on an allow-list.</div>
                  </div>
                </div>
                <h3 className="gk-card__title">Who can transcribe</h3>
                {settings.who_may_use === "allowlist" || settings.who_may_invite === "allowlist" ? (
                  <GrantsSection
                    orgId={org.id}
                    grants={settings.grants}
                    whoMayUse={settings.who_may_use}
                    whoMayInvite={settings.who_may_invite}
                    onChanged={load}
                  />
                ) : (
                  <p className="gk-sm">
                    {settings.who_may_use === "members"
                      ? "Every member can start transcription. Switch to “Specific people” to hand the right out one by one."
                      : "Only owners and admins can start transcription."}{" "}
                    Guests never can.
                  </p>
                )}
              </div>
            </Card>

            {/* FR-BY15: covers every slot, the language model included, so it sits with access
                rather than under speech-to-text. */}
            <Card title="Members' own models">
              <ToggleRow
                switchPosition="start"
                label="Allow members to use their own models"
                description="Members can connect their own provider account (Account → AI models). It's used before this org's account and the hosted default. Keys are stored encrypted, same as org accounts. Off by default."
                checked={settings.allow_personal_models}
                onChange={(v) => void patchSettings({ allow_personal_models: v })}
              />
            </Card>
            {saveFeedback}
          </>
        ) : null}
      </div>
    </div>
  );
}

export type TranscriptionSettingsPatch = {
  enabled?: boolean;
  diarization_enabled?: boolean;
  who_may_use?: TranscriptionWhoMayUse;
  who_may_invite?: TranscriptionWhoMayInvite;
  allow_personal_models?: boolean;
};

/** PATCH /orgs/{id}/transcription; returns the persisted settings (incl. re-resolved slots). */
export function saveTranscriptionSettings(
  orgId: string,
  body: TranscriptionSettingsPatch,
): Promise<TranscriptionSettingsOut> {
  return api<TranscriptionSettingsOut>("PATCH", `/orgs/${orgId}/transcription`, body);
}

/** A slot row's On/Off maps onto the org's per-slot switch: STT → `enabled`,
 * speaker labels → `diarization_enabled`. Org off switches stay authoritative over hosted. */
export function slotTogglePatch(slot: "stt" | "diarization", enabled: boolean): TranscriptionSettingsPatch {
  return slot === "stt" ? { enabled } : { diarization_enabled: enabled };
}

export function slotResolvedLabel(slotOut: TranscriptionSlotOut): string {
  if (slotOut.status !== "available") return slotOut.reason ?? "Unavailable";
  if (slotOut.provenance === "hosted") return `Hosted · ${slotOut.label ?? "Orchardrun"}`;
  return `${slotOut.label || kindLabel(slotOut.kind ?? "")} · org account`;
}

/** F4: a resolved, configurable slot (STT / speaker labels) in the DS 1h slot-row layout. */
function ConfigurableSlotRow({
  orgId,
  slot,
  icon,
  slotOut,
  onChange,
  onToggleEnabled,
}: {
  orgId: string;
  slot: "stt" | "diarization";
  icon: string;
  slotOut: TranscriptionSlotOut;
  /** Opens the Speech-to-text tab at this slot's providers. */
  onChange: () => void;
  onToggleEnabled: (next: boolean) => void;
}) {
  const [testing, setTesting] = useState(false);
  const [test, setTest] = useState<TranscriptionTestOut | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function onTest() {
    setTesting(true);
    setError(null);
    setTest(null);
    try {
      const result = await api<TranscriptionTestOut>("POST", `/orgs/${orgId}/transcription/slots/${slot}/test`);
      setTest(result);
    } catch (err) {
      setError(err instanceof ApiFail ? err.message : "Couldn't test that slot.");
    } finally {
      setTesting(false);
    }
  }

  const available = slotOut.status === "available";
  return (
    <div className="ai-slot-group">
      <SlotRow
        icon={icon}
        role={SLOT_LABEL[slot]}
        name={slotOut.enabled ? slotResolvedLabel(slotOut) : "Off"}
        badge={
          !available ? (
            <Badge tone="outline">Not connected</Badge>
          ) : slotOut.provenance === "hosted" ? (
            <Badge tone="outline">Hosted</Badge>
          ) : (
            <Badge tone="accent">Org account</Badge>
          )
        }
        note={
          slotOut.kind ? (
            <>
              {kindLabel(slotOut.kind)}
              {slotOut.api_key_last4 ? <span className="mono"> · •••• {slotOut.api_key_last4}</span> : null}
            </>
          ) : slot === "stt" ? (
            "Connect a provider on the Speech-to-text tab to transcribe recordings."
          ) : (
            "Speaker labels need an Orchardrun account or the hosted default."
          )
        }
        status={
          slotOut.enabled && available ? (
            <Badge tone="success" dot>
              Connected
            </Badge>
          ) : !slotOut.enabled ? (
            <Badge icon="pause">Turned off</Badge>
          ) : null
        }
      />
      {test ? (
        <Notice tone={test.ok ? "default" : "error"}>
          {test.ok
            ? `Reached ${test.endpoint} with ${test.configured_model} — sent a silent one-second sample through the real transcription path.`
            : (test.error ?? "Test failed.")}
        </Notice>
      ) : null}
      {error ? <Notice tone="error">{error}</Notice> : null}
      <div className="admin-provider-actions">
        <Button variant="ghost" onClick={onChange}>
          Change
        </Button>
        <Button
          variant="secondary"
          onClick={() => void onTest()}
          loading={testing}
          disabled={!slotOut.enabled || !available}
        >
          Test
        </Button>
        <Button variant="ghost" onClick={() => onToggleEnabled(!slotOut.enabled)}>
          {slotOut.enabled ? "Off" : "On"}
        </Button>
      </div>
    </div>
  );
}

/** F1: the language model's Overview row. Same layout as the other slots; it has no hosted
 * default (FR-AI2a), so "not connected" is the normal starting state, not an error. */
function LanguageModelSlotRow({ slotOut, onChange }: { slotOut: TranscriptionSlotOut | null; onChange: () => void }) {
  const available = slotOut?.status === "available";
  const kind = slotOut?.kind ? (LLM_KIND_LABEL[slotOut.kind as keyof typeof LLM_KIND_LABEL] ?? slotOut.kind) : null;
  return (
    <div className="ai-slot-group">
      <SlotRow
        icon="sparkle"
        role="Language model"
        name={available ? `${slotOut?.label || kind || "Language model"} · org account` : "Summaries, action items, chapters and Ask"}
        badge={available ? <Badge tone="accent">Org account</Badge> : <Badge tone="outline">Not connected</Badge>}
        note={
          available && kind ? (
            <>
              {kind}
              {slotOut?.api_key_last4 ? <span className="mono"> · •••• {slotOut.api_key_last4}</span> : null}
            </>
          ) : (
            "Meeting notes need a language model. There is no hosted default: nothing is sent to a model until one is connected on the Language model tab."
          )
        }
        status={
          available ? (
            <Badge tone="success" dot>
              Connected
            </Badge>
          ) : null
        }
      />
      <div className="admin-provider-actions">
        <Button variant="ghost" onClick={onChange}>
          {available ? "Change" : "Connect a model"}
        </Button>
      </div>
    </div>
  );
}

/** One provider slot (DS 1h): what it does, who provides it, and whether it works. */
function SlotRow({
  icon,
  role,
  name,
  badge,
  note,
  status,
  unavailable,
}: {
  icon: string;
  role: string;
  name: string;
  badge: ReactNode;
  note: ReactNode;
  status?: ReactNode;
  unavailable?: boolean;
}) {
  return (
    <div role="listitem" aria-label={role} className={`ai-slot${unavailable ? " is-unavailable" : ""}`}>
      <span className="ai-slot__icon">
        <Icon name={icon} />
      </span>
      <div className="ai-slot__body">
        <div className="gk-overline">{role}</div>
        <div className="ai-slot__name">
          <span>{name}</span>
          {badge}
        </div>
        <div className="gk-sm">{note}</div>
      </div>
      {status ? <div className="ai-slot__end">{status}</div> : null}
    </div>
  );
}

/** One slot's providers on the Speech-to-text tab. `focus` scrolls it into view when the
 * Overview's Change or View details asked for this slot. */
function ProvidersCard({
  orgId,
  slot,
  providers,
  focus,
  onChanged,
}: {
  orgId: string;
  slot: "stt" | "diarization";
  providers: TranscriptionProviderOut[];
  focus: boolean;
  onChanged: () => void;
}) {
  const id = `ai-providers-${slot}`;
  useEffect(() => {
    // Speech-to-text is already near the top; only speaker labels needs bringing into view.
    // (jsdom has no scrollIntoView; a real browser always does.)
    if (focus && slot !== "stt") document.getElementById(id)?.scrollIntoView?.({ block: "start" });
  }, [focus, slot, id]);
  return (
    <Card as="section" title={`${SLOT_LABEL[slot]} providers`} id={id}>
      <ProvidersSection orgId={orgId} slot={slot} providers={providers} onChanged={onChanged} />
    </Card>
  );
}

function ProvidersSection({
  orgId,
  slot,
  providers,
  onChanged,
}: {
  orgId: string;
  slot: "stt" | "diarization";
  providers: TranscriptionProviderOut[];
  onChanged: () => void;
}) {
  const kinds = SLOT_KINDS[slot];
  const [kind, setKind] = useState<TranscriptionProviderKind>(kinds[0]);
  const [label, setLabel] = useState("");
  const [apiBaseUrl, setApiBaseUrl] = useState(KIND_DEFAULTS[kinds[0]].base);
  const [modelId, setModelId] = useState(KIND_DEFAULTS[kinds[0]].model);
  const [apiKey, setApiKey] = useState("");
  const [connecting, setConnecting] = useState(false);
  const [connectError, setConnectError] = useState<string | null>(null);
  const [disconnectId, setDisconnectId] = useState<string | null>(null);
  const [connectOpen, setConnectOpen] = useState(false);

  function closeConnect() {
    onKindChange(kinds[0]);
    setLabel("");
    setApiKey("");
    setConnectError(null);
    setConnectOpen(false);
  }

  function onKindChange(next: TranscriptionProviderKind) {
    setKind(next);
    const defaults = KIND_DEFAULTS[next];
    setApiBaseUrl(defaults.base);
    setModelId(defaults.model);
  }

  async function onConnect() {
    setConnecting(true);
    setConnectError(null);
    try {
      await api("POST", `/orgs/${orgId}/transcription/providers`, {
        slot,
        kind,
        label: label.trim(),
        api_base_url: apiBaseUrl.trim() || undefined,
        model_id: modelId.trim() || undefined,
        api_key: apiKey.trim(),
      });
      setApiKey("");
      setLabel("");
      setConnectOpen(false);
      onChanged();
    } catch (err) {
      setConnectError(err instanceof ApiFail ? err.message : "Couldn't connect that provider.");
    } finally {
      setConnecting(false);
    }
  }

  async function onDisconnectConfirm() {
    if (!disconnectId) return;
    const id = disconnectId;
    setDisconnectId(null);
    try {
      await api("DELETE", `/orgs/${orgId}/transcription/providers/${id}`);
      onChanged();
    } catch (err) {
      setConnectError(err instanceof ApiFail ? err.message : "Couldn't disconnect that provider.");
    }
  }

  return (
    <div>
      <div className="admin-row-sub" style={{ marginBottom: 12 }}>
        API keys are write-only. Grackle stores them encrypted and never shows them again — only the
        last four characters. Disconnecting a provider does not silently activate another.
      </div>

      {providers.length === 0 ? (
        <div className="admin-row-sub" style={{ marginBottom: 14 }}>
          No {SLOT_LABEL[slot].toLowerCase()} provider connected. The product is complete without one.
        </div>
      ) : (
        <div className="admin-provider-list">
          {providers.map((p) => (
            <ProviderCard key={p.id} orgId={orgId} provider={p} onChanged={onChanged} onDisconnect={() => setDisconnectId(p.id)} />
          ))}
        </div>
      )}

      {connectOpen ? (
        <div className="admin-provider-connect">
          <h4 className="admin-subhead">Connect a {SLOT_LABEL[slot].toLowerCase()} provider</h4>
          <Select
            id={`provider-kind-${slot}`}
            className="admin-field--narrow"
            label="Kind"
            value={kind}
            onChange={(v) => onKindChange(v as TranscriptionProviderKind)}
            options={kinds.map((k) => ({ value: k, label: KIND_LABEL[k] }))}
          />
          <div className="admin-two-col">
            <Field
              label="Label (optional)"
              placeholder="Production Whisper"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
            />
            <Field label="Model id" value={modelId} onChange={(e) => setModelId(e.target.value)} />
          </div>
          <Field
            label="API base URL"
            placeholder="https://api.openai.com/v1"
            value={apiBaseUrl}
            onChange={(e) => setApiBaseUrl(e.target.value)}
          />
          <Field
            label="API key"
            type="password"
            autoComplete="off"
            value={apiKey}
            onChange={(e) => setApiKey(e.target.value)}
            help="Stored encrypted. Never returned after save."
          />
          {connectError ? <Notice tone="error">{connectError}</Notice> : null}
          <div className="admin-provider-actions">
            <Button onClick={() => void onConnect()} loading={connecting} disabled={!apiKey.trim()}>
              Connect
            </Button>
            <Button variant="ghost" onClick={closeConnect} disabled={connecting}>
              Cancel
            </Button>
          </div>
        </div>
      ) : (
        <div className="admin-stack">
          {connectError ? <Notice tone="error">{connectError}</Notice> : null}
          <div>
            <Button variant="secondary" icon="plus" onClick={() => setConnectOpen(true)}>
              Connect a provider
            </Button>
          </div>
        </div>
      )}

      <ConfirmDialog
        open={disconnectId !== null}
        title="Disconnect provider"
        message="Disconnect this provider? In-flight jobs stop using it. Another connected provider is not activated automatically."
        confirmLabel="Disconnect"
        danger
        onClose={() => setDisconnectId(null)}
        onConfirm={() => void onDisconnectConfirm()}
      />
    </div>
  );
}

function ProviderCard({
  orgId,
  provider,
  onChanged,
  onDisconnect,
}: {
  orgId: string;
  provider: TranscriptionProviderOut;
  onChanged: () => void;
  onDisconnect: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [label, setLabel] = useState(provider.label);
  const [apiBaseUrl, setApiBaseUrl] = useState(provider.api_base_url);
  const [modelId, setModelId] = useState(provider.model_id);
  const [apiKey, setApiKey] = useState("");
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [activating, setActivating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [test, setTest] = useState<TranscriptionTestOut | null>(null);

  useEffect(() => {
    setLabel(provider.label);
    setApiBaseUrl(provider.api_base_url);
    setModelId(provider.model_id);
    setApiKey("");
  }, [provider]);

  async function onSave() {
    setSaving(true);
    setError(null);
    try {
      await api("PATCH", `/orgs/${orgId}/transcription/providers/${provider.id}`, {
        label: label.trim(),
        api_base_url: apiBaseUrl.trim(),
        model_id: modelId.trim(),
        api_key: apiKey.trim() ? apiKey.trim() : undefined,
      });
      setApiKey("");
      setEditing(false);
      onChanged();
    } catch (err) {
      setError(err instanceof ApiFail ? err.message : "Couldn't save that provider.");
    } finally {
      setSaving(false);
    }
  }

  async function onTest() {
    setTesting(true);
    setError(null);
    setTest(null);
    try {
      const result = await api<TranscriptionTestOut>(
        "POST",
        `/orgs/${orgId}/transcription/providers/${provider.id}/test`,
      );
      setTest(result);
    } catch (err) {
      setError(err instanceof ApiFail ? err.message : "Couldn't reach that provider.");
    } finally {
      setTesting(false);
    }
  }

  async function onActivate() {
    setActivating(true);
    setError(null);
    try {
      await api("POST", `/orgs/${orgId}/transcription/providers/${provider.id}/activate`);
      onChanged();
    } catch (err) {
      setError(err instanceof ApiFail ? err.message : "Couldn't activate that provider.");
    } finally {
      setActivating(false);
    }
  }

  return (
    <Card size="sm" className="admin-provider">
      <div className="admin-provider-card-head">
        <div>
          <div className="admin-row-name">{provider.label || kindLabel(provider.kind)}</div>
          <div className="admin-row-sub">
            {kindLabel(provider.kind)} · <span className="mono">{provider.model_id}</span>
          </div>
        </div>
        <div className="admin-provider-card-meta">
          <span className="mono admin-provider-last4" title="Last four of the stored key">
            •••• {provider.api_key_last4}
          </span>
          {provider.is_active ? <Badge size="sm" tone="success">Active</Badge> : null}
        </div>
      </div>

      {editing ? (
        <div style={{ marginTop: 12 }}>
          <div className="admin-two-col">
            <Field label="Label" value={label} onChange={(e) => setLabel(e.target.value)} />
            <Field label="Model id" value={modelId} onChange={(e) => setModelId(e.target.value)} />
          </div>
          <Field label="API base URL" value={apiBaseUrl} onChange={(e) => setApiBaseUrl(e.target.value)} />
          <Field
            label="API key"
            type="password"
            autoComplete="off"
            placeholder="Unchanged — leave blank to keep it"
            value={apiKey}
            onChange={(e) => setApiKey(e.target.value)}
          />
        </div>
      ) : (
        <div className="admin-row-sub" style={{ marginTop: 8 }}>
          <span className="mono">{provider.api_base_url}</span>
        </div>
      )}

      {test ? (
        <Notice tone={test.ok ? "default" : "error"}>
          {test.ok
            ? `Reached ${test.endpoint} with ${test.configured_model} — sent a silent one-second sample through the real transcription path.`
            : (test.error ?? "Test failed.")}
        </Notice>
      ) : null}
      {error ? <Notice tone="error">{error}</Notice> : null}

      <div className="admin-provider-actions">
        {editing ? (
          <>
            <Button onClick={() => void onSave()} loading={saving}>
              Save
            </Button>
            <Button variant="ghost" onClick={() => setEditing(false)}>
              Cancel
            </Button>
          </>
        ) : (
          <Button variant="ghost" onClick={() => setEditing(true)}>
            Edit
          </Button>
        )}
        <Button variant="secondary" onClick={() => void onTest()} loading={testing}>
          Test connection
        </Button>
        {!provider.is_active ? (
          <Button variant="secondary" onClick={() => void onActivate()} loading={activating}>
            Make active
          </Button>
        ) : null}
        <Button variant="ghost" onClick={onDisconnect}>
          Disconnect
        </Button>
      </div>
    </Card>
  );
}

// F1 (SD36, brief §2): "Language model" row — the org-scoped LLM slot, read through
// `?slot=llm` so it never shows up in the STT providers list or vice versa. The
// `allow_personal_models` toggle on the Access tab covers this slot too (brief: "reuse the
// existing BYO toggle"). `onChanged` re-reads the page's settings so the Overview's language
// model row and flags follow a connect, activate or turn-off here.
function LanguageModelSection({
  orgId,
  lastNotesError,
  onChanged,
}: {
  orgId: string;
  lastNotesError: LastNotesErrorOut | null;
  onChanged: () => void;
}) {
  const [providers, setProviders] = useState<LlmProvidersList | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  const loadProviders = useCallback(() => {
    api<LlmProvidersList>("GET", `/orgs/${orgId}/transcription/providers?slot=llm`)
      .then((next) => {
        setProviders(next);
        setLoadError(null);
      })
      .catch((err) => setLoadError(err instanceof ApiFail ? err.message : "Couldn't load the language model."));
  }, [orgId]);

  useEffect(() => {
    loadProviders();
  }, [loadProviders]);

  function load() {
    loadProviders();
    onChanged();
  }

  if (loadError && !providers) {
    return (
      <div>
        <h4 className="admin-subhead">Language model</h4>
        <Notice tone="error">{loadError}</Notice>
        <Button variant="secondary" onClick={loadProviders}>
          Retry
        </Button>
      </div>
    );
  }

  if (!providers) return <div className="admin-row-sub">Loading…</div>;

  return (
    <div className="admin-stack">
      <div className="admin-row-sub">
        Powers meeting notes — summary, decisions, action items and chapters, every line sourced
        back to the transcript. No hosted default: nothing runs until a model is connected here.
      </div>
      <LlmProviderPicker
        providers={providers.items}
        // Only while notes are actually off: no model connected, or none of them active.
        copyLine={
          providers.items.length === 0
            ? "Grackle has no built-in language model. Meeting notes stay off until you connect one."
            : providers.items.some((p) => p.is_active)
              ? undefined
              : "No model is active, so meeting notes are off. Activate one to turn them on."
        }
        connectCollapsed
        onConnect={async (body: LlmConnectBody) => {
          await api("POST", `/orgs/${orgId}/transcription/providers?slot=llm`, body);
          load();
        }}
        onUpdate={async (id: string, body: LlmUpdateBody) => {
          await api("PATCH", `/orgs/${orgId}/transcription/providers/${id}`, body);
          load();
        }}
        onDelete={async (id: string) => {
          await api("DELETE", `/orgs/${orgId}/transcription/providers/${id}`);
          load();
        }}
        onTest={(id: string) => api<LlmTestOut>("POST", `/orgs/${orgId}/transcription/providers/${id}/test`)}
        onActivate={async (id: string) => {
          await api("POST", `/orgs/${orgId}/transcription/providers/${id}/activate`);
          load();
        }}
      />
      {lastNotesError?.error ? (
        <Callout
          tone={lastNotesError.recent_failures >= NOTES_FAILURES_BROKEN ? "danger" : "now"}
          icon={lastNotesError.recent_failures >= NOTES_FAILURES_BROKEN ? "warning-circle" : "warning"}
        >
          {notesErrorText(lastNotesError)}
          {lastNotesError.grackle_id ? (
            <>
              {" "}
              <Link to={`/g/${lastNotesError.grackle_id}`}>Open the recording</Link> to retry or cancel.
            </>
          ) : null}
        </Callout>
      ) : null}
    </div>
  );
}

// F11: the org's AI off switch for Ask, and its per-user hourly question limit (a DB setting,
// not an env var). GET/PUT /orgs/{org_id}/ask/settings (owners/admins). The page loads the
// settings (the Overview's flags read them too) and passes them in.
function AskSettingsSection({
  orgId,
  settings,
  loadError,
  onSaved,
}: {
  orgId: string;
  settings: AskSettingsOut | null;
  loadError: string | null;
  onSaved: (next: AskSettingsOut) => void;
}) {
  const [rate, setRate] = useState(settings ? String(settings.ask_rate_per_hour) : "");
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (settings) setRate(String(settings.ask_rate_per_hour));
  }, [settings]);

  async function save(body: Partial<AskSettingsOut>) {
    setError(null);
    setSaved(false);
    try {
      onSaved(await putAskSettings(orgId, body));
      setSaved(true);
    } catch (err) {
      setError(err instanceof ApiFail ? err.message : "Couldn't save that.");
    }
  }

  if (!settings) {
    return loadError ? <Notice tone="error">{loadError}</Notice> : <div className="admin-row-sub">Loading…</div>;
  }
  const rateNum = Number(rate);
  const rateValid = Number.isInteger(rateNum) && rateNum >= 1 && rateNum <= 10000;

  return (
    <div>
      <ToggleRow
        switchPosition="start"
        label="Allow Ask across meetings"
        description="Members can ask questions answered from the meetings they can already open, with sources. Needs a language model; with none connected Ask says so. Questions and answers are never stored."
        checked={settings.ask_across_meetings}
        onChange={(v) => void save({ ask_across_meetings: v })}
      />
      <div className="ai-inline-field">
        <Field
          label="Questions per person per hour"
          type="number"
          min={1}
          max={10000}
          value={rate}
          onChange={(e) => setRate(e.target.value)}
          error={rateValid ? undefined : "Enter a whole number from 1 to 10000."}
        />
        <Button
          variant="secondary"
          disabled={!rateValid || rateNum === settings.ask_rate_per_hour}
          onClick={() => void save({ ask_rate_per_hour: rateNum })}
        >
          Save limit
        </Button>
      </div>
      {error ? <Notice tone="error">{error}</Notice> : null}
      {saved ? <div className="gk-caption ai-saved">Saved.</div> : null}
    </div>
  );
}

function GrantsSection({
  orgId,
  grants,
  whoMayUse,
  whoMayInvite,
  onChanged,
}: {
  orgId: string;
  grants: TranscriptionGrantOut[];
  whoMayUse: TranscriptionWhoMayUse;
  whoMayInvite: TranscriptionWhoMayInvite;
  onChanged: () => void;
}) {
  const [error, setError] = useState<string | null>(null);

  async function patchGrant(userId: string, body: { can_transcribe?: boolean; can_invite?: boolean }) {
    setError(null);
    try {
      await api("PATCH", `/orgs/${orgId}/members/${userId}/transcription`, body);
      onChanged();
    } catch (err) {
      setError(err instanceof ApiFail ? err.message : "Couldn't update that grant.");
    }
  }

  return (
    <div>
      <div className="admin-row-sub" style={{ marginBottom: 10 }}>
        Owners and admins always hold both rights. Guests never do. A checked box is the grant
        itself — turning the policy back to “admins only” keeps the grant stored but unused.
      </div>
      {error ? <Notice tone="error">{error}</Notice> : null}
      <Card size="flush" className="admin-table-card">
<table className="admin-table">
        <thead>
          <tr>
            <th className="gk-overline">Name</th>
            <th className="gk-overline">Role</th>
            {whoMayUse === "allowlist" ? <th className="gk-overline">May transcribe</th> : null}
            {whoMayInvite === "allowlist" ? <th className="gk-overline">May invite</th> : null}
          </tr>
        </thead>
        <tbody>
          {grants.map((g) => {
            const grantable = g.role === "member";
            const implicit = g.role === "owner" || g.role === "admin";
            return (
              <tr key={g.user_id}>
                <td>
                  <div className="admin-row-name">{g.name || g.email}</div>
                  <div className="admin-row-sub">{g.email}</div>
                </td>
                <td>
                  <Badge size="sm">{g.role}</Badge>
                </td>
                {whoMayUse === "allowlist" ? (
                  <td>
                    {implicit ? (
                      <span className="admin-row-sub">Always</span>
                    ) : g.role === "guest" ? (
                      <span className="admin-row-sub">Never</span>
                    ) : (
                      <label className="admin-grant-check">
                        <input
                          type="checkbox"
                          checked={g.can_transcribe}
                          disabled={!grantable}
                          onChange={(e) => void patchGrant(g.user_id, { can_transcribe: e.target.checked })}
                        />
                        <span>{g.can_transcribe ? "Yes" : "No"}</span>
                      </label>
                    )}
                  </td>
                ) : null}
                {whoMayInvite === "allowlist" ? (
                  <td>
                    {implicit ? (
                      <span className="admin-row-sub">Always</span>
                    ) : g.role === "guest" ? (
                      <span className="admin-row-sub">Never</span>
                    ) : (
                      <label className="admin-grant-check">
                        <input
                          type="checkbox"
                          checked={g.can_invite}
                          disabled={!grantable}
                          onChange={(e) => void patchGrant(g.user_id, { can_invite: e.target.checked })}
                        />
                        <span>{g.can_invite ? "Yes" : "No"}</span>
                      </label>
                    )}
                  </td>
                ) : null}
              </tr>
            );
          })}
        </tbody>
      </table>
</Card>
    </div>
  );
}
